import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Map, { Marker, Popup, NavigationControl, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl from 'maplibre-gl';
import { InputNumber } from 'primereact/inputnumber';
import { Panel } from 'primereact/panel';
import { Paginator } from 'primereact/paginator';
import { ActionButton } from '../ActionButton';
import InputTextComponent from '../inputs/InputTextComponent';
import { t } from 'i18next';
import {buildNominatimSearchParams, nominatimParamsToQueryString} from '../../utils/NominatimUtils';
import {
    buildToleranceCircleGeoJson,
    destinationPoint,
    getToleranceMeters,
} from '../../utils/GeoMapUtils';

export const AddressComponents = {
    CITY: "CITY",
    STREET_NAME: "STREET_NAME",
    STREET_NUMBER: "STREET_NUMBER",
    HOUSE_NUMBER: "HOUSE_NUMBER",
    POSTAL_CODE: "POSTAL_CODE",
};

const computeInitialViewState = (locations, initLocation) => {
    const valid = (locations || []).filter(
        (l) =>
            l?.latitude != null &&
            l?.longitude != null &&
            !Number.isNaN(Number(l.latitude)) &&
            !Number.isNaN(Number(l.longitude))
    );
    if (valid.length > 0) {
        const lng = valid.reduce((sum, l) => sum + Number(l.longitude), 0) / valid.length;
        const lat = valid.reduce((sum, l) => sum + Number(l.latitude), 0) / valid.length;
        return { longitude: lng, latitude: lat, zoom: valid.length === 1 ? 14 : 10 };
    }
    return { longitude: initLocation[1], latitude: initLocation[0], zoom: 6 };
};

const fitMapToLocations = (mapRef, locations) => {
    if (!mapRef?.current || !locations?.length) {
        return;
    }
    const map = mapRef.current.getMap?.() ?? mapRef.current;
    if (!map?.fitBounds) {
        return;
    }
    const bounds = new maplibregl.LngLatBounds();
    let hasBounds = false;
    locations.forEach((loc) => {
        const lat = Number(loc.latitude);
        const lng = Number(loc.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return;
        }
        bounds.extend([lng, lat]);
        hasBounds = true;
        const meters = getToleranceMeters(loc.localizationTolerance);
        [0, 90, 180, 270].forEach((bearing) => {
            const p = destinationPoint(lat, lng, meters, bearing);
            bounds.extend([p.lon, p.lat]);
        });
    });
    if (hasBounds) {
        map.resize?.();
        map.fitBounds(bounds, { padding: 40, maxZoom: 16, duration: 0 });
    }
};

export const LocationsComponentV2 = (props) => {
    const {
        locations, onAddLocation, addressInfoComponents,
        onUpdateLocation, viewMode, validator, showErrorMessage,
        showSuccessMessage, initLocation, style
    } = props;
    const mapRef = useRef(null);
    const [city, setCity] = useState('');
    const [streetName, setStreetName] = useState('');
    const [streetNumber, setStreetNumber] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [loading, setLoading] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [tableFirst, setTableFirst] = useState(0);
    const tableRows = 20;

    const addMarker = (event) => {
        if (viewMode !== 'VIEW') {
            const lat = event.lngLat.lat;
            const lng = event.lngLat.lng;

            const precision = 18;
            const existingLocation = locations.find(
                (location) =>
                    parseFloat(location.latitude).toFixed(precision) === lat.toFixed(precision) &&
                    parseFloat(location.longitude).toFixed(precision) === lng.toFixed(precision)
            );

            if (existingLocation) {
                showErrorMessage(t("global.locationAlreadyAdded"));
                return;
            }

            const newMarker = {
                latitude: lat,
                longitude: lng,
                localizationTolerance: 0.5,
                id: undefined,
            };
            onAddLocation(newMarker);

            // fly to new location
            if (mapRef.current) {
                mapRef.current.flyTo({
                    center: [lng, lat],
                    zoom: 14,
                    duration: 1000,
                });
            }
        }
    };

    const handleMarkerDrag = (index, event) => {
        if (viewMode !== 'VIEW') {
            setIsDragging(true);
            const lat = event.lngLat.lat;
            const lng = event.lngLat.lng;
            const updatedMarkers = [...locations];
            updatedMarkers[index] = {
                ...updatedMarkers[index],
                latitude: lat,
                longitude: lng,
            };
            onUpdateLocation(updatedMarkers);
            setTimeout(() => setIsDragging(false), 100); // debounce re-fitBounds
        }
    };
    const removeMarker = (index) => {
        const updatedMarkers = [...locations];
        updatedMarkers.splice(index, 1);
        onUpdateLocation(updatedMarkers);
    };

    const onToleranceChange = (index, event) => {
        const updatedMarkers = [...locations];
        updatedMarkers[index].localizationTolerance = event.value;
        onUpdateLocation(updatedMarkers);
    };

    const zoomToMarker = (index) => {
        const loc = locations[index];
        if (!loc || !mapRef.current) {
            return;
        }
        mapRef.current.flyTo({
            center: [loc.longitude, loc.latitude],
            zoom: 14,
            duration: 800,
        });
        setSelectedMarker(index);
    };

    const searchAddress = async (form) => {
        const queryString = nominatimParamsToQueryString(buildNominatimSearchParams(form));
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&${queryString}`);
            return await response.json();
        } catch (error) {
            console.error('Błąd podczas wyszukiwania adresu:', error);
            return null;
        }
    };
  
    const [mapLoaded, setMapLoaded] = useState(false);

    useEffect(() => {
        if (locations.length > 0 && mapLoaded && !isDragging) {
            fitMapToLocations(mapRef, locations);
        }
    }, [locations, mapLoaded, isDragging]);

    useEffect(() => {
        // gdy lista się skróci (np. usunięcie), upewnij się, że paginacja nie wychodzi poza zakres
        if (tableFirst >= locations.length && tableFirst !== 0) {
            const lastPageFirst = Math.max(0, Math.floor((locations.length - 1) / tableRows) * tableRows);
            setTableFirst(lastPageFirst);
        }
    }, [locations.length]);

    
    return (
        
        <div className="row">
            {viewMode !== 'VIEW' && (
                <>
                    <Panel className="col-lg-4 col-md-12 mt-4" header="Informacja">
                        <div className="">
                            <ul className="p-0 m-0" style={{listStyle: 'none'}}>
                                <li className="mb-2">
                                    <i className="mdi mdi-map-marker-plus mr-2" /> {t('global.clickOnMapToAddPoint')}
                                </li>
                                <li className="mb-2">
                                    <i className="mdi mdi-magnify mr-2" /> {t('global.useAddressSearch')}
                                </li>
                                <li className="mb-2">
                                    <i className="mdi mdi-hand-back-right mr-2" /> {t('global.dragMarkerToPrecisePosition')}
                                </li>
                                <li>
                                    <i className="mdi mdi-radar mr-2" /> {t('global.setRadiusOfLocation')}
                                </li>
                            </ul>
                        </div>
                    </Panel>
                    <Panel className="col-lg-8 col-md-12 mt-4" header={t('global.address')}>
                        <div className="">
                            <div className="row">
                                <InputTextComponent
                                    id="city"
                                    name="city"
                                    label={t('partner.city')}
                                    colClass="col-lg-8 col-md-8 col-sm-12"
                                    value={city}
                                    validator={validator}
                                    validators="required|max:255"
                                    onChange={(a, b, c, d) => {
                                        setCity(c.currentTarget?.value);
                                    }}
                                    rendered={!!addressInfoComponents.includes(AddressComponents.CITY)}
                                    viewMode={viewMode}
                                />
                                <InputTextComponent
                                    id="postalCode"
                                    name="postalCode"
                                    label={t('address.postalCode')}
                                    colClass="col-lg-4 col-md-4 col-sm-12"
                                    value={postalCode}
                                    validator={validator}
                                    validators="zipcode|required"
                                    onChange={(a, b, c, d) => {
                                        setPostalCode(c.currentTarget?.value);
                                    }}
                                    rendered={!!addressInfoComponents.includes(AddressComponents.POSTAL_CODE)}
                                    viewMode={viewMode}
                                />
                            </div>
                            <div className="row">
                                <InputTextComponent
                                    id="streetName"
                                    name="streetName"
                                    label={t('partner.streetName')}
                                    colClass="col-lg-6 col-md-6 col-sm-12"
                                    value={streetName}
                                    validator={validator}
                                    validators="required|max:64"
                                    onChange={(a, b, c, d) => {
                                        setStreetName(c.currentTarget?.value);
                                    }}
                                    rendered={!!addressInfoComponents.includes(AddressComponents.STREET_NAME)}
                                    viewMode={viewMode}
                                />
                                <InputTextComponent
                                    id="streetNumber"
                                    name="streetNumber"
                                    label={t('partner.streetNumber')}
                                    colClass="col-lg-3 col-md-3 col-sm-6"
                                    value={streetNumber}
                                    validator={validator}
                                    validators="required|max:64"
                                    onChange={(a, b, c, d) => {
                                        setStreetNumber(c.currentTarget?.value);
                                    }}
                                    rendered={!!addressInfoComponents.includes(AddressComponents.STREET_NUMBER)}
                                    viewMode={viewMode}
                                />
                                <InputTextComponent
                                    id="houseNumber"
                                    name="houseNumber"
                                    label={t('partner.houseNumber')}
                                    colClass="col-lg-3 col-md-3 col-sm-6"
                                    value={houseNumber}
                                    validator={validator}
                                    validators="not_required"
                                    onChange={(a, b, c, d) => {
                                        setHouseNumber(c.currentTarget?.value);
                                    }}
                                    rendered={!!addressInfoComponents.includes(AddressComponents.HOUSE_NUMBER)}
                                    viewMode={viewMode}
                                />
                            </div>
                            <ActionButton
                                id="search-location-btn"
                                rendered={viewMode !== 'VIEW'}
                                label={'Wyszukaj'}
                                handleClick={() => {
                                    if (validator.allValid()) {
                                        searchAddress({
                                            country: 'Poland',
                                            city,
                                            streetName,
                                            streetNumber,
                                            postalCode,
                                            houseNumber,
                                        }).then((results) => {
                                            if (results && results.length > 0) {
                                                const lat = parseFloat(results[0].lat);
                                                const lng = parseFloat(results[0].lon);
                                                addMarker({ lngLat: { lat, lng } });
                                                setCity('');
                                                setStreetName('');
                                                setStreetNumber('');
                                                setPostalCode('');
                                                setHouseNumber('');
                                                showSuccessMessage(t("global.locationSuccessfulAdded"));
                                            } else {
                                                showErrorMessage(t('global.locationNotFound'));
                                            }
                                        });
                                    } else {
                                        validator.showMessages();                                    
                                        setLoading(!loading);

                                    }
                                }}
                            />
                        </div>
                    </Panel>
                </>
            )}
            <div className={`col-12 mt-4 mb-4`}>
                <Map
                    ref={mapRef}
                    initialViewState={computeInitialViewState(locations, initLocation)}
                    style={style}
                    mapStyle={process.env.REACT_APP_MAP_STYLE}
                    onClick={addMarker}
                    onLoad={() => {
                        setMapLoaded(true);
                        requestAnimationFrame(() => fitMapToLocations(mapRef, locations));
                    }}
                >
                    
                    <NavigationControl position="top-right" />
                  
                    {locations.map((location, index) => {
                     return <React.Fragment key={index}>
                            <Marker
                                key={index}
                                longitude={location.longitude}
                                latitude={location.latitude}
                                draggable={viewMode !== 'VIEW'}
                                onDragEnd={(event) => {
                                    handleMarkerDrag(index, event)}}
                                onClick={(e) => {
                                    e.originalEvent.stopPropagation()
                                    e.originalEvent.preventDefault()
                                    setSelectedMarker(index)
                                }}
                            />
                            <Source
                                id={`circles-source-${index}`}
                                type="geojson"
                                data={buildToleranceCircleGeoJson(
                                    location.latitude,
                                    location.longitude,
                                    getToleranceMeters(location.localizationTolerance)
                                )}
                            >
                                <Layer
                                    id={`circle-fill-${index}`}
                                    type="fill"
                                    paint={{
                                        'fill-color': 'rgba(66, 135, 245, 0.4)',
                                    }}
                                />
                                <Layer
                                    id={`circle-outline-${index}`}
                                    type="line"
                                    paint={{
                                        'line-color': '#4287f5',
                                        'line-width': 2,
                                    }}
                                />
                            </Source>
                      </React.Fragment>   
                    })}

                    {selectedMarker !== null && locations[selectedMarker] && (
                        <Popup 
                        className='w-6rem'
                            longitude={locations[selectedMarker].longitude}
                            latitude={locations[selectedMarker].latitude}
                            onClose={() => setSelectedMarker(null)}
                            
                        >
                            <div>
                                {locations[selectedMarker]?.displayName && (
                                    <div className='mb-2'>
                                        {locations[selectedMarker].displayName}
                                    </div>
                                )}
                                <label htmlFor='tolerance' className='font-bold block mb-2'>
                                    {t("global.distanceTolerance")}
                                </label>
                                <InputNumber showButtons buttonLayout="horizontal" step={0.05}
                                    incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                    inputId='tolerance'
                                    minFractionDigits={2}
                                    min={0}
                                    disabled={viewMode === 'VIEW'}
                                    label={t("global.tolerance")}
                                    className="mb-4 pr-8 pl-4"
                                    inputStyle={{ textAlign: 'right', width:'90px' }}
                                    value={locations[selectedMarker].localizationTolerance}
                                    onChange={(e) => onToleranceChange(selectedMarker, e)}
                                />
                                <br />
                                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                <ActionButton
                                    rendered={viewMode !== 'VIEW'}
                                    label={t("global.remove")}
                                     iconName='mdi-delete'
                                    className="p-button-danger delete-btn"
                                    handleClick={() => removeMarker(selectedMarker)}
                                />
                            </div>
                            </div>
                        </Popup>
                    )}
                </Map>
            </div>
            <div className={`col-12`}>
                <Panel className="mb-4" header={t('global.locations') || 'Lokalizacje'}>
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('global.latitude')}</th>
                                    <th>{t('global.longitude')}</th>
                                    <th>{t('global.radius')} [km]</th>
                                    <th>{t('global.displayName')}</th>
                                    <th>{t('global.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {locations && locations.length > 0 ? (
                                    locations.slice(tableFirst, tableFirst + tableRows).map((loc, idx) => (
                                        <tr key={idx}>
                                            <td>{tableFirst + idx + 1}</td>
                                            <td>{typeof loc.latitude === 'number' ? loc.latitude.toFixed(6) : loc.latitude}</td>
                                            <td>{typeof loc.longitude === 'number' ? loc.longitude.toFixed(6) : loc.longitude}</td>
                                            <td>{typeof loc.localizationTolerance === 'number' ? loc.localizationTolerance.toFixed(2) : loc.localizationTolerance}</td>
                                            <td>{loc.displayName || '-'}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <ActionButton
                                                        rendered
                                                        label={t('global.zoom')}
                                                        iconName='mdi-airplane'
                                                        className="p-button"
                                                        handleClick={() => zoomToMarker(tableFirst + idx)}
                                                    />
                                                    {viewMode !== 'VIEW' && (
                                                        <ActionButton
                                                            rendered
                                                            label={t('global.remove')}
                                                            iconName='mdi-delete'
                                                            className="p-button-danger delete-btn"
                                                            handleClick={() => removeMarker(tableFirst + idx)}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6}>{t('global.noData') || 'Brak danych'}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {locations && locations.length > tableRows && (
                        <div className="mt-2">
                            <Paginator
                                first={tableFirst}
                                rows={tableRows}
                                totalRecords={locations.length}
                                onPageChange={(e) => setTableFirst(e.first)}
                                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            />
                        </div>
                    )}
                </Panel>
            </div>
        </div>
    );
};

LocationsComponentV2.defaultProps = {
    viewMode: 'VIEW',
    locations: [],
    style: { width: '100%', height: '700px', zIndex: '100' },
    singleLocation: false,
    initLocation: [52.237, 19.017],
    addressInfoComponents: [AddressComponents.CITY, AddressComponents.STREET_NAME, AddressComponents.POSTAL_CODE, AddressComponents.STREET_NUMBER, AddressComponents.HOUSE_NUMBER],
};

LocationsComponentV2.propTypes = {
    viewMode: PropTypes.string,
    initLocation: PropTypes.array,
    singleLocation: PropTypes.bool,
    onAddLocation: PropTypes.func.isRequired,
    showErrorMessage: PropTypes.func.isRequired,
    validator: PropTypes.object.isRequired,
    style: PropTypes.object,
    onUpdateLocation: PropTypes.func.isRequired,
    locations: PropTypes.array,
    addressInfoComponents: PropTypes.array,
};

export default LocationsComponentV2;
